import React, { useEffect, useRef, useState } from 'react';
import { Button }  from 'src/components/ui/button';
import { Input }   from 'src/components/ui/input';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Mail, Inbox, RefreshCw, Paperclip, Flag, EyeOff, Eye,
  Trash2, Reply, ReplyAll, Forward, ChevronLeft, PenLine,
  X, Send, AlertCircle, MailOpen, MailCheck, ChevronDown,
} from 'lucide-react';

// ─── Carpetas disponibles ────────────────────────────────────────────────────

const FOLDERS = [
  { id: 'inbox',   label: 'Recibidos',  icon: Inbox,    graphName: 'inbox' },
  { id: 'sent',    label: 'Enviados',   icon: Send,     graphName: 'sentItems' },
  { id: 'drafts',  label: 'Borradores', icon: PenLine,  graphName: 'drafts' },
  { id: 'deleted', label: 'Eliminados', icon: Trash2,   graphName: 'deletedItems' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function parseResponse(res) {
  const text = await res.text();
  try { return { data: JSON.parse(text), text }; }
  catch { return { data: null, text }; }
}

async function safeErrorMessage(res, fallback = 'Error desconocido') {
  try {
    const { data, text } = await parseResponse(res);
    return data?.error || data?.message || text || fallback;
  } catch { return fallback; }
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

function senderName(email) {
  return email?.from?.emailAddress?.name
    || email?.from?.emailAddress?.address
    || 'Desconocido';
}

function senderAvatar(email) {
  const name = email?.from?.emailAddress?.name || '?';
  return name[0].toUpperCase();
}

// ─── EmailViewer ──────────────────────────────────────────────────────────────

function EmailViewer({ email, mailbox, backendBase, authHeaders, onAction, onClose }) {
  const [composing, setComposing] = useState(null);
  const [composeText, setComposeText] = useState('');
  const [composeTo, setComposeTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendOk, setSendOk] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    setComposing(null);
    setComposeText('');
    setComposeTo('');
    setSendError('');
    setSendOk(false);
  }, [email?.id]);

  const doAction = async (action, payload = {}) => {
    try {
      const res = await fetch(`${backendBase}/api/email/actions`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, messageId: email.id, mailbox, payload }),
      });
      if (!res.ok) {
        const { data } = await parseResponse(res);
        console.error('[email action]', data?.error || res.status);
        return false;
      }
      return true;
    } catch (e) {
      console.error('[email action]', e?.message);
      return false;
    }
  };

  const handleMarkRead = async () => {
    const ok = await doAction(email.isRead ? 'markUnread' : 'markRead');
    if (ok) onAction('toggleRead', email.id);
  };

  const handleFlag = async () => {
    const isFlagged = (email?.flag?.flagStatus || '').toLowerCase() === 'flagged';
    const ok = await doAction(isFlagged ? 'unflag' : 'flag');
    if (ok) onAction('toggleFlag', email.id);
  };

  const handleTrash = async () => {
    const ok = await doAction('trash');
    if (ok) { onAction('trash', email.id); onClose(); }
  };

  const handleSend = async () => {
    if (!composeText.trim()) return;
    setSending(true);
    setSendError('');
    // reply and forward both pass explicit recipients so Graph doesn't
    // add unintended addresses (shared-mailbox app-only auth quirk).
    // replyAll lets Graph resolve all recipients natively.
    const toAddresses = composing !== 'replyAll'
      ? composeTo.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    const ok = await doAction(composing === 'replyAll' ? 'replyAll' : composing, {
      comment: composeText,
      to: toAddresses,
    });
    setSending(false);
    if (ok) { setSendOk(true); setComposing(null); setComposeText(''); setComposeTo(''); }
    else setSendError('No se pudo enviar. Verifica la configuración de Graph API.');
  };

  const isFlagged = (email?.flag?.flagStatus || '').toLowerCase() === 'flagged';
  const bodyHtml  = email?.body?.content || '';
  const isHtml    = email?.body?.contentType?.toLowerCase() === 'html';

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-4 py-2.5 border-b border-border/60 bg-background/80 flex-wrap">
        <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden" onClick={onClose} title="Volver">
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1 flex-1 flex-wrap">
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => { setComposing('reply'); setComposeTo(email?.from?.emailAddress?.address || ''); }}>
            <Reply className="h-3.5 w-3.5" /> Responder
          </Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setComposing('replyAll')}>
            <ReplyAll className="h-3.5 w-3.5" /> Todos
          </Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => { setComposing('forward'); setComposeTo(''); }}>
            <Forward className="h-3.5 w-3.5" /> Reenviar
          </Button>

          <div className="w-px h-5 bg-border/60 mx-1 hidden sm:block" />

          <Button variant="ghost" size="icon" className="h-8 w-8" title={email?.isRead ? 'Marcar no leído' : 'Marcar leído'} onClick={handleMarkRead}>
            {email?.isRead ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className={`h-8 w-8 ${isFlagged ? 'text-amber-500' : ''}`} title={isFlagged ? 'Quitar marcador' : 'Marcar'} onClick={handleFlag}>
            <Flag className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-[var(--yool-error)]" title="Eliminar" onClick={handleTrash}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        {sendOk && (
          <span className="text-xs text-[var(--yool-success)] flex items-center gap-1">
            <MailCheck className="h-3.5 w-3.5" /> Enviado
          </span>
        )}
      </div>

      {/* Email header */}
      <div className="px-5 py-4 border-b border-border/40">
        <h2 className="text-base font-semibold leading-snug mb-2">
          {email?.subject || '(Sin asunto)'}
        </h2>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
              style={{ background: 'var(--fy-indigo)' }}>
              {senderAvatar(email)}
            </div>
            <div>
              <p className="text-sm font-medium leading-none">{senderName(email)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{email?.from?.emailAddress?.address}</p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {email?.receivedDateTime
              ? new Date(email.receivedDateTime).toLocaleString('es-CL', {
                  day: '2-digit', month: 'long', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })
              : ''}
          </div>
        </div>
        {Array.isArray(email?.toRecipients) && email.toRecipients.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            <span className="font-medium">Para: </span>
            {email.toRecipients.map(r => r?.emailAddress?.address).join(', ')}
          </p>
        )}
        {email?.hasAttachments && Array.isArray(email?.attachments) && email.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {email.attachments.map((att, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-border/60 bg-muted/40">
                <Paperclip className="h-3 w-3" />
                {att?.name || 'Adjunto'}
                {att?.size && <span className="text-muted-foreground ml-1">({Math.round(att.size / 1024)}KB)</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Compose panel */}
      {composing && (
        <div className="px-5 py-3 border-b border-border/60 bg-muted/20 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {composing === 'reply' ? '↩ Responder' : composing === 'replyAll' ? '↩ Responder a todos' : '→ Reenviar'}
            </span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setComposing(null)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
          {/* Show "Para:" for reply (pre-filled) and forward (empty) — not for replyAll */}
          {(composing === 'reply' || composing === 'forward') && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0 w-8">Para:</span>
              <Input
                placeholder="email@destino.com, otro@destino.com"
                value={composeTo}
                onChange={e => setComposeTo(e.target.value)}
                className="text-sm h-8 flex-1"
              />
            </div>
          )}
          <textarea
            className="w-full text-sm rounded-lg border border-border/60 bg-background p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[var(--fy-indigo)]/20 min-h-[80px]"
            placeholder="Escribe tu mensaje…"
            value={composeText}
            onChange={e => setComposeText(e.target.value)}
          />
          {sendError && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {sendError}
            </p>
          )}
          <div className="flex gap-2">
            <Button size="sm" className="h-8 gap-1.5" style={{ background: 'var(--fy-indigo)' }}
              onClick={handleSend} disabled={sending || !composeText.trim() || (composing === 'forward' && !composeTo.trim())}>
              <Send className="h-3.5 w-3.5" />
              {sending ? 'Enviando…' : 'Enviar'}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setComposing(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Email body */}
      <div className="flex-1 overflow-auto">
        {isHtml ? (
          <iframe
            ref={iframeRef}
            srcDoc={bodyHtml}
            sandbox="allow-same-origin allow-popups"
            className="w-full h-full min-h-[400px] border-none"
            title="email-body"
            onLoad={() => {
              try {
                const doc = iframeRef.current?.contentDocument;
                if (doc) {
                  const h = doc.documentElement.scrollHeight;
                  if (h > 0) iframeRef.current.style.height = h + 'px';
                }
              } catch (_) {}
            }}
          />
        ) : (
          <pre className="p-5 text-sm whitespace-pre-wrap font-sans text-foreground">
            {email?.body?.content || email?.bodyPreview || ''}
          </pre>
        )}
      </div>
    </div>
  );
}

// ─── EmailClient (main) ───────────────────────────────────────────────────────

export default function EmailClient() {
  const [sharedMailboxes,  setSharedMailboxes]  = useState([]);
  const [loadingMailboxes, setLoadingMailboxes] = useState(false);
  const [mailboxesError,   setMailboxesError]   = useState('');
  const [accountOpen,      setAccountOpen]      = useState(false);

  const [selectedMailbox,  setSelectedMailbox]  = useState('');
  const [selectedFolder,   setSelectedFolder]   = useState('inbox');
  const [emails,           setEmails]           = useState([]);
  const [loadingEmails,    setLoadingEmails]     = useState(false);
  const [emailsError,      setEmailsError]      = useState('');

  const [selectedEmail,    setSelectedEmail]    = useState(null);
  const [mobileView,       setMobileView]       = useState('list');

  const [search, setSearch] = useState('');

  const abortRef           = useRef(null);
  const accountDropdownRef = useRef(null);

  const origin      = typeof window !== 'undefined' ? window.location.origin : '';
  const backendBase = (process.env.REACT_APP_BACKEND_URL || origin).replace(/\/$/, '');
  const isLocalhost = origin.includes('localhost');

  const graphToken  = process.env.REACT_APP_MICROSOFT_GRAPH_ACCESS_TOKEN
    || process.env.REACT_APP_GRAPH_ACCESS_TOKEN || '';
  const authHeaders = graphToken ? { Authorization: `Bearer ${graphToken}` } : {};

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // Cierra el dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── fetch helpers ─────────────────────────────────────────────────────────

  const fetchMailboxes = async () => {
    setLoadingMailboxes(true);
    setMailboxesError('');
    const endpoints = [
      `${backendBase}/api/email/mailboxes`,
      `${backendBase}/api/mailboxes`,
    ];
    let lastError = 'No se pudo obtener la lista de buzones.';
    for (const url of endpoints) {
      try {
        const res = await fetch(url, { headers: authHeaders, cache: 'no-store' });
        if (res.ok) {
          const { data } = await parseResponse(res);
          setSharedMailboxes(Array.isArray(data?.shared) ? data.shared : []);
          setLoadingMailboxes(false);
          return;
        }
        if (res.status !== 404) lastError = await safeErrorMessage(res, lastError);
      } catch (err) {
        if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) continue;
        lastError = err?.message || lastError;
      }
    }
    setMailboxesError(lastError);
    setLoadingMailboxes(false);
  };

  const fetchEmails = async (mailbox = '', folder = 'inbox') => {
    // Cancel any in-flight request immediately
    abortRef.current?.abort();

    if (!mailbox) { setEmails([]); setLoadingEmails(false); return; }
    if (!isValidEmail(mailbox)) {
      setEmails([]);
      setEmailsError('Email no válido.');
      setLoadingEmails(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Clear stale data immediately so old account emails never bleed through
    setEmails([]);
    setEmailsError('');
    setLoadingEmails(true);

    const query = `?mailbox=${encodeURIComponent(mailbox)}&folder=${folder}&top=50`;
    const url   = `${backendBase}/api/email/emails${query}`;

    try {
      const res = await fetch(url, {
        headers: authHeaders,
        cache: 'no-store',
        signal: controller.signal,
      });
      // If this controller was superseded by a newer call, discard result
      if (controller.signal.aborted) return;
      if (res.ok) {
        const { data } = await parseResponse(res);
        setEmails(Array.isArray(data?.emails) ? data.emails : []);
      } else {
        setEmailsError(await safeErrorMessage(res, 'No se pudo obtener los correos.'));
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setEmailsError(err?.message || 'No se pudo obtener los correos.');
    } finally {
      // Only update loading state if this request is still the active one
      if (!controller.signal.aborted) setLoadingEmails(false);
    }
  };

  useEffect(() => { fetchMailboxes(); }, []);

  const handleMailboxSelect = (mailboxEmail) => {
    setSelectedMailbox(mailboxEmail);
    setSelectedFolder('inbox');
    setSelectedEmail(null);
    setSearch('');
    setAccountOpen(false);
    if (mailboxEmail) fetchEmails(mailboxEmail, 'inbox');
    else setEmails([]);
  };

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setSelectedEmail(null);
    setSearch('');
    if (selectedMailbox) fetchEmails(selectedMailbox, folder);
  };

  const handleSelectEmail = (email) => {
    setSelectedEmail(email);
    setMobileView('viewer');
    if (!email.isRead) {
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, isRead: true } : e));
    }
  };

  const handleAction = (type, emailId) => {
    if (type === 'toggleRead') {
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, isRead: !e.isRead } : e));
      if (selectedEmail?.id === emailId) setSelectedEmail(prev => ({ ...prev, isRead: !prev.isRead }));
    }
    if (type === 'toggleFlag') {
      setEmails(prev => prev.map(e => {
        if (e.id !== emailId) return e;
        const cur = (e?.flag?.flagStatus || '').toLowerCase() === 'flagged';
        return { ...e, flag: { flagStatus: cur ? 'notFlagged' : 'flagged' } };
      }));
    }
    if (type === 'trash') {
      setEmails(prev => prev.filter(e => e.id !== emailId));
    }
  };

  // ── derived ───────────────────────────────────────────────────────────────

  const filtered = search.trim()
    ? emails.filter(e =>
        (e?.subject || '').toLowerCase().includes(search.toLowerCase()) ||
        (e?.from?.emailAddress?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (e?.from?.emailAddress?.address || '').toLowerCase().includes(search.toLowerCase()) ||
        (e?.bodyPreview || '').toLowerCase().includes(search.toLowerCase())
      )
    : emails;

  const unread   = emails.filter(e => e?.isRead === false).length;
  const flagged  = emails.filter(e => (e?.flag?.flagStatus || '').toLowerCase() === 'flagged').length;
  const attached = emails.filter(e => e?.hasAttachments).length;

  const validMailboxes  = sharedMailboxes.filter(m => !!m?.mail);
  const currentMailbox  = validMailboxes.find(m => m.mail === selectedMailbox);
  const currentFolder   = FOLDERS.find(f => f.id === selectedFolder) || FOLDERS[0];

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-4 md:-m-6">

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-border/60 bg-background shrink-0">
        {/* Title */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="p-1.5 rounded-lg" style={{ background: 'var(--fy-indigo)', opacity: 0.9 }}>
            <Mail className="h-4 w-4 text-white" />
          </div>
          <h1 className="text-sm font-semibold whitespace-nowrap">Bandeja de Correo</h1>
        </div>

        {/* Account selector — amplio, en el top bar */}
        <div className="relative flex-1 max-w-sm" ref={accountDropdownRef}>
          {loadingMailboxes ? (
            <Skeleton className="h-9 w-full rounded-lg" />
          ) : (
            <>
              <button
                onClick={() => setAccountOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors text-left min-h-[36px]"
              >
                <div className="h-6 w-6 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: selectedMailbox ? 'var(--fy-indigo)' : 'var(--fy-slate)' }}>
                  {selectedMailbox
                    ? (currentMailbox?.displayName || selectedMailbox)[0].toUpperCase()
                    : '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate leading-none">
                    {currentMailbox?.displayName || (selectedMailbox ? selectedMailbox.split('@')[0] : 'Seleccionar buzón')}
                  </p>
                  {selectedMailbox && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">{selectedMailbox}</p>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
              </button>

              {accountOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-background border border-border/60 rounded-lg shadow-lg overflow-hidden min-w-full max-h-64 overflow-y-auto">
                  {mailboxesError && (
                    <p className="text-xs text-red-500 px-3 py-2 flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />{mailboxesError}
                    </p>
                  )}
                  {!mailboxesError && validMailboxes.length === 0 && (
                    <p className="text-xs text-muted-foreground px-3 py-3">Sin buzones configurados</p>
                  )}
                  {validMailboxes.map(m => (
                    <button
                      key={m.id || m.mail}
                      onClick={() => handleMailboxSelect(m.mail)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0 ${
                        selectedMailbox === m.mail ? 'bg-muted/30' : ''
                      }`}
                    >
                      <div className="h-7 w-7 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white"
                        style={{ background: selectedMailbox === m.mail ? 'var(--fy-indigo)' : 'var(--fy-slate)' }}>
                        {(m.displayName || m.mail)[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate leading-none font-medium">{m.displayName || m.mail}</p>
                        {m.displayName && m.mail !== m.displayName && (
                          <p className="text-[11px] text-muted-foreground truncate mt-0.5">{m.mail}</p>
                        )}
                      </div>
                      {selectedMailbox === m.mail && (
                        <div className="h-2 w-2 rounded-full shrink-0" style={{ background: 'var(--fy-indigo)' }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* KPI pills + refresh */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden md:flex items-center gap-2 mr-1">
            {[
              { label: `${unread} no leídos`,     color: 'var(--fy-indigo)', show: unread > 0 },
              { label: `${flagged} marcados`,      color: 'var(--fy-orange)', show: flagged > 0 },
              { label: `${attached} con adjuntos`, color: 'var(--fy-slate)',  show: attached > 0 },
            ].filter(p => p.show).map(p => (
              <span key={p.label} className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ color: p.color, background: p.color + '18' }}>
                {p.label}
              </span>
            ))}
          </div>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={() => { fetchMailboxes(); if (selectedMailbox) fetchEmails(selectedMailbox, selectedFolder); }}
            disabled={loadingMailboxes || loadingEmails}>
            <RefreshCw className={`h-3.5 w-3.5 ${(loadingMailboxes || loadingEmails) ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
        </div>
      </div>

      {/* ── 3-panel body ────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Panel 1: Carpetas ── */}
        <div className={`w-36 shrink-0 border-r border-border/60 flex flex-col bg-muted/20 ${
          mobileView !== 'list' ? 'hidden md:flex' : 'flex'
        }`}>
          <nav className="p-2 flex-1">
            {selectedMailbox ? (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 mb-1.5 mt-1">Carpetas</p>
                <div className="space-y-0.5">
                  {FOLDERS.map(f => {
                    const isActive = selectedFolder === f.id;
                    const badge = f.id === 'inbox' && unread > 0 ? unread : null;
                    return (
                      <button
                        key={f.id}
                        onClick={() => handleFolderSelect(f.id)}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-colors text-left ${
                          isActive ? 'font-semibold text-white' : 'hover:bg-muted/60 text-foreground'
                        }`}
                        style={isActive ? { background: 'var(--fy-indigo)' } : {}}
                      >
                        <f.icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1 text-xs">{f.label}</span>
                        {badge && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                            isActive ? 'bg-white/20 text-white' : 'bg-muted text-foreground'
                          }`}>{badge}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                <Mail className="h-6 w-6 text-muted-foreground/30" />
                <p className="text-[11px] text-muted-foreground leading-snug px-2">
                  Selecciona una cuenta
                </p>
              </div>
            )}
          </nav>
        </div>

        {/* ── Panel 2: Lista de correos ── */}
        <div className={`flex flex-col border-r border-border/60 overflow-hidden ${
          selectedEmail ? 'w-72 shrink-0 hidden md:flex' : 'flex-1'
        } ${mobileView === 'viewer' ? 'hidden' : 'flex'}`}>

          {/* Search */}
          <div className="p-2.5 border-b border-border/40">
            <Input
              placeholder={`Buscar en ${currentFolder.label.toLowerCase()}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {/* Header de carpeta */}
          <div className="px-3 py-1.5 border-b border-border/30 flex items-center gap-1.5">
            <currentFolder.icon className="h-3 w-3 text-muted-foreground" />
            <span className="text-[11px] font-medium text-muted-foreground">
              {currentFolder.label}
            </span>
            {filtered.length > 0 && (
              <span className="text-[11px] text-muted-foreground">
                · {filtered.length} mensaje{filtered.length !== 1 ? 's' : ''}
              </span>
            )}
            {selectedMailbox && (
              <span className="ml-auto text-[10px] font-medium truncate max-w-[100px]"
                style={{ color: 'var(--fy-indigo)' }}>
                {selectedMailbox.split('@')[0]}
              </span>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {emailsError && (
              <div className="flex items-start gap-2 text-sm text-red-500 p-3">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="text-xs">{emailsError}</span>
              </div>
            )}
            {loadingEmails ? (
              <div className="space-y-1 p-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="p-2.5 space-y-1.5">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                ))}
              </div>
            ) : !selectedMailbox ? (
              <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
                <Mail className="h-8 w-8 opacity-20" />
                <span className="text-sm">Selecciona una cuenta</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
                <MailOpen className="h-8 w-8 opacity-30" />
                <span className="text-sm">{currentFolder.label} vacío</span>
              </div>
            ) : (
              filtered.map((email) => {
                const isSelected = selectedEmail?.id === email.id;
                const isFl = (email?.flag?.flagStatus || '').toLowerCase() === 'flagged';
                return (
                  <button
                    key={email.id}
                    onClick={() => handleSelectEmail(email)}
                    className={`w-full text-left px-3 py-2.5 border-b border-border/30 transition-colors border-l-2 ${
                      isSelected
                        ? 'bg-muted/50'
                        : 'hover:bg-muted/40 border-l-transparent'
                    }`}
                    style={isSelected ? { borderLeftColor: 'var(--fy-indigo)' } : {}}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5"
                        style={{ background: email.isRead ? 'var(--fy-slate)' : 'var(--fy-indigo)' }}>
                        {senderAvatar(email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1 mb-0.5">
                          <span className={`text-xs truncate ${!email.isRead ? 'font-bold' : 'font-medium'}`}>
                            {senderName(email)}
                          </span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(email.receivedDateTime)}</span>
                        </div>
                        <div className={`text-xs truncate ${!email.isRead ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {email.subject || '(Sin asunto)'}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                          {email.bodyPreview}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {!email.isRead && (
                            <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'var(--fy-indigo)' }} />
                          )}
                          {email.hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground" />}
                          {isFl && <Flag className="h-3 w-3" style={{ color: 'var(--fy-orange)' }} />}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Panel 3: Visor ── */}
        <div className={`flex-1 overflow-hidden bg-background ${
          selectedEmail
            ? (mobileView === 'viewer' ? 'flex flex-col' : 'hidden md:flex md:flex-col')
            : 'hidden md:flex md:items-center md:justify-center'
        }`}>
          {selectedEmail ? (
            <EmailViewer
              email={selectedEmail}
              mailbox={selectedMailbox}
              backendBase={backendBase}
              authHeaders={authHeaders}
              onAction={handleAction}
              onClose={() => { setSelectedEmail(null); setMobileView('list'); }}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <div className="p-4 rounded-full bg-muted/40">
                <Mail className="h-8 w-8 opacity-30" />
              </div>
              <p className="text-sm">Selecciona un correo para leerlo</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
